/**
 * MessageNotification - Toast popup for new stage transmissions
 * Slides in from the right, auto-dismisses after 5 seconds
 */

import { useEffect } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MessageNotificationProps {
  count: number;
  stageName: string;
  isVisible: boolean;
  onDismiss: () => void;
  onView: () => void;
}

export function MessageNotification({
  count,
  stageName,
  isVisible,
  onDismiss,
  onView,
}: MessageNotificationProps) {
  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(onDismiss, 5000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onDismiss]);

  return (
    <div
      className={cn(
        'fixed top-20 right-4 z-50 transform transition-all duration-300 ease-out',
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      )}
    >
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#12356B]/30 bg-[#E2E5EC] shadow-lg min-w-[280px] max-w-[360px]">
        {/* Icon */}
        <div className="size-10 rounded-full bg-[#12356B]/15 text-[#12356B] flex items-center justify-center flex-shrink-0">
          <MessageSquare className="size-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-[#12356B]">
            {count} new transmission{count !== 1 ? 's' : ''}
          </p>
          <p className="text-sm text-slate-600">{stageName}</p>
        </div>

        {/* View button */}
        <button
          onClick={() => {
            onView();
            onDismiss();
          }}
          className="text-sm font-semibold text-[#12356B] hover:text-[#12356B] px-2 py-1 rounded hover:bg-white/70 transition-colors flex-shrink-0"
        >
          View
        </button>

        {/* Dismiss */}
        <button
          onClick={onDismiss}
          className="size-6 rounded-full flex items-center justify-center flex-shrink-0 text-[#12356B] hover:bg-white/70 transition-colors"
          aria-label="Dismiss"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
